import { Box } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import { Tabs, TABS_CONTENT_GAP } from '@repo/ui'

import { INSIGHTS_TAB_PATHS, INSIGHTS_TABS, insightsTabOf, type InsightsTab } from 'routes'

import { ArticlesTab, ContentIdeasTab, RulesTab } from '../GtmArticlesPage'
import { ContentAnalyticsPanel } from './analytics/ContentAnalyticsPanel'
import { VerafiedPanel } from './verafied/VerafiedPanel'

const TAB_LABELS: Record<InsightsTab, string> = {
  analytics: 'Analytics',
  articles: 'Articles',
  interviews: 'Interviews',
  ideas: 'Ideas',
  rules: 'Rules',
}

const TABS = INSIGHTS_TABS.map((value) => ({ value, label: TAB_LABELS[value] }))

export const INSIGHTS_TITLE = 'Insights'

/**
 * VRG-751 (Spencer, 2026-10-23) — GTM → Insights (channel #8, "Content marketing" until the
 * rename). The tab is the PATH (`/internal/gtm/insights/<tab>`), so a link opens the tab it names
 * and the sidebar row stays selected across all four. Old addresses redirect in one hop
 * (`routes/gtmRedirects.ts`).
 *
 * ORDER, revised 2026-10-29 (Spencer): Analytics · Articles · Interviews · Ideas, and Analytics is
 * the landing — the row order and the index agree, see `INSIGHTS_TABS`. The dispatch below falls
 * THROUGH to Analytics for the same reason `insightsTabOf` falls back to it: one default, declared
 * once, so the two cannot drift.
 *
 * ONE tab row. Until VRG-751 the Articles tab rendered a page carrying its OWN row (Articles ·
 * Ideas · VERAfied guests, VRG-740), so two rows stacked and the same object — a guest — had two
 * lists. Ideas is a top-level tab because ideas feed BOTH articles and interviews — one pipeline,
 * one set of statuses, with `type` (Explainer / Perspective / VERAfied) saying what the idea
 * becomes; the second guest list is retired by VRG-752.
 *
 * Each tab renders the page Header itself, with this title and this tab row, so each keeps its own
 * header actions (one primary, rightmost, §26).
 */
export const GtmInsightsPage = () => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const tab = insightsTabOf(pathname)

  const tabs = (
    <Box sx={{ mb: `${TABS_CONTENT_GAP}px` }}>
      <Tabs
        redirect={false}
        tabs={TABS}
        value={tab}
        onChange={(v: string) => navigate(INSIGHTS_TAB_PATHS[v as InsightsTab])}
      />
    </Box>
  )

  if (tab === 'articles') return <ArticlesTab title={INSIGHTS_TITLE} tabs={tabs} />
  if (tab === 'interviews') return <VerafiedPanel title={INSIGHTS_TITLE} tabs={tabs} />
  if (tab === 'ideas') return <ContentIdeasTab title={INSIGHTS_TITLE} tabs={tabs} />
  if (tab === 'rules') return <RulesTab title={INSIGHTS_TITLE} tabs={tabs} />
  return <ContentAnalyticsPanel title={INSIGHTS_TITLE} tabs={tabs} />
}
